import { useCallback, useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import { FormattedMessage } from 'react-intl';

import { joinCommunity } from 'mastodon/actions/communities';
import type { ApiCommunityJSON } from 'mastodon/api_types/communities';
import { useAppDispatch } from 'mastodon/store';

/**
 * Join / accept-invite / request-to-join controls for a community the viewer
 * is not (yet) a member of. Shared between the desktop context panel and the
 * community show column (the only join surface on mobile, and the landing
 * spot for invite notifications).
 */
export const CommunityJoinControls: FC<{
  community: ApiCommunityJSON;
}> = ({ community }) => {
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [requestNoteOpen, setRequestNoteOpen] = useState(false);
  const [requestNote, setRequestNote] = useState('');

  const communityId = community.id;
  const joinMode = community.join_mode;
  const isInvited = community.viewer_state === 'invited';

  const handleJoinClick = useCallback(() => {
    // For by_request communities, expose a textarea so the requester can
    // attach a short note that mods will see in their notification feed.
    // An invited account joins directly, so no note is needed.
    if (joinMode === 'by_request' && !isInvited && !requestNoteOpen) {
      setRequestNoteOpen(true);
      setActionError(null);
      return;
    }
    setBusy(true);
    setActionError(null);
    dispatch(
      joinCommunity(
        communityId,
        joinMode === 'by_request' && requestNote.trim()
          ? requestNote.trim()
          : undefined,
      ),
    )
      .catch((e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        setActionError(err.response?.data?.error ?? 'Failed to join');
      })
      .finally(() => {
        setBusy(false);
        setRequestNoteOpen(false);
        setRequestNote('');
      });
  }, [
    dispatch,
    communityId,
    joinMode,
    isInvited,
    requestNote,
    requestNoteOpen,
  ]);

  const cancelJoinNote = useCallback(() => {
    setRequestNoteOpen(false);
    setRequestNote('');
  }, []);

  const handleNoteChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setRequestNote(e.target.value);
    },
    [],
  );

  if (community.viewer_state === 'member') return null;

  return (
    <div className='community-join-controls'>
      {actionError && (
        <div className='community-context-panel__error'>{actionError}</div>
      )}

      {community.viewer_state === 'banned' && (
        <span className='community-banned-badge'>
          <FormattedMessage id='communities.banned' defaultMessage='Banned' />
        </span>
      )}

      {community.viewer_state === 'requested' && (
        <span className='community-pending-badge'>
          <FormattedMessage
            id='communities.request_pending'
            defaultMessage='Request pending'
          />
        </span>
      )}

      {community.viewer_state !== 'banned' &&
        community.viewer_state !== 'requested' && (
          <>
            {requestNoteOpen && (
              <div className='community-context-panel__request-note'>
                <label htmlFor='community-join-note'>
                  <FormattedMessage
                    id='communities.join_note_label'
                    defaultMessage='Add a note for the moderators (optional):'
                  />
                </label>
                <textarea
                  id='community-join-note'
                  value={requestNote}
                  onChange={handleNoteChange}
                  maxLength={500}
                  rows={3}
                  placeholder='e.g. why you want to join'
                />
              </div>
            )}
            <button
              type='button'
              className='button'
              onClick={handleJoinClick}
              disabled={busy}
            >
              {isInvited ? (
                <FormattedMessage
                  id='communities.invited'
                  defaultMessage='Accept invite'
                />
              ) : joinMode === 'by_request' && requestNoteOpen ? (
                <FormattedMessage
                  id='communities.send_request'
                  defaultMessage='Send request'
                />
              ) : (
                <FormattedMessage id='communities.join' defaultMessage='Join' />
              )}
            </button>
            {requestNoteOpen && (
              <button
                type='button'
                className='button button-tertiary'
                onClick={cancelJoinNote}
                disabled={busy}
              >
                <FormattedMessage
                  id='communities.cancel'
                  defaultMessage='Cancel'
                />
              </button>
            )}
          </>
        )}
    </div>
  );
};
